import {
  Autocomplete,
  Button,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import CustomNumberInput from "components/CustomNumberInput";
import AddLocationModal from "components/lists/locations/AddLocationModal";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import { ENTITY } from "constants/entities";
import { DATE_VIEWS, STATUS_CODES } from "constants/index";
import {
  ARTICLES_URL,
  LOCATIONS_URL,
} from "constants/urls";
import { dateISOFormat, normDate, normStr } from "helpers";
import { toFloat } from "helpers/formatNumber";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useEffect, useMemo, useCallback, useState } from "react";
import { RxPlus } from "react-icons/rx";
import {
  Article,
  ArticleLocation,
  IncomeMovementArticleRowProps,
  LocationBase,
  MovementArticle,
} from "types";

type NewArticle = Omit<
  Article,
  "id" | "client" | "container" | "unit" | "volume_weight"
>;
type AvailabilityStatus = "AVAILABLE" | "NOT_AVAILABLE";

export default function AddMovementArticleRow({
  movement,
  articleInput,
  rowIndex,
  onMovementArticleAdd,
  locations,
  stores,
  showLote,
  showOC,
  showFechaVencimiento,
  sublocations,
}: IncomeMovementArticleRowProps) {
  const { showSnackBar } = useToast();
  const [amount, setAmount] = useState<number>(0);
  const [selectedLocation, setSelectedLocation] = useState<{
    id: number | undefined;
    label: string;
  }>({ id: undefined, label: "" });
  const [dueDate, setDueDate] = useState<string | undefined>(undefined);
  const [ordenCompra, setOrdenCompra] = useState<string | undefined>(undefined);
  const [lote, setLote] = useState<string | undefined>(undefined);
  const [addNewLocationModalOpen, setAddNewLocationModalOpen] =
    useState<boolean>(false);
  const [amountPerContainer, setAmountPerContainer] = useState<string>(
    articleInput.amount_per_container.toString(),
  );
  const [availabilityStatus, setAvailabilityStatus] =
    useState<AvailabilityStatus>("AVAILABLE");
  const [observation, setObservation] = useState<string>("");
  const [selectedSubLocation, setSelectedSubLocation] = useState<{
    id: number | undefined;
    label: string;
  }>({ id: undefined, label: "" });

  const { createData: createLocation } = useCrud<LocationBase>(LOCATIONS_URL, {
    disableAutoFetch: true,
  });
  const { createData: createArticle } = useCrud<NewArticle>(ARTICLES_URL, {
    disableAutoFetch: true,
  });

  const { addSessionData } = useSession();

  const locationOptions = useMemo(() => {
    return locations
      .map(({ id, code }) => ({ id, label: code }))
      .concat({ id: 0, label: "Nueva Ubicación" });
  }, [locations]);

  const mainLocationId = useMemo(() => {
    if (!movement.store_id) return null;

    const store = stores.find((s) => s.id === movement.store_id);
    return store?.main_location_id ?? null;
  }, [movement.store_id, stores]);

  const availableSubLocations = useMemo(() => {
    const parentId = selectedLocation?.id;
    if (!parentId || parentId === 0) return [];
    return sublocations.filter(
      (sl) => sl.parent_location_id === parentId && sl.consolidated_at == null,
    );
  }, [selectedLocation?.id, sublocations]);

  const subLocationOptions = useMemo(() => {
    return availableSubLocations.map((sl) => ({ id: sl.id, label: sl.code }));
  }, [availableSubLocations]);

  const isSubLocationEnabled = availableSubLocations.length > 0;

  const handleCreateLocation = async (newLocation: LocationBase) => {
    const response = await createLocation(newLocation, true, true);

    if (response.status === STATUS_CODES.OK) {
      const createdLocation = response.data;
      addSessionData(ENTITY.LOCATIONS, response.data);
      setSelectedLocation({
        id: createdLocation.id,
        label: createdLocation.code,
      });
      setAddNewLocationModalOpen(false);
      showSnackBar("Se ha agregado la ubicación correctamente.", "success");
    } else {
      showSnackBar(
        "No se ha podido agregar la ubicación. Por favor intente nuevamente.",
        "error",
      );
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(parseInt(e.target.value));
  };

  const handleLocationChange = (
    _e: unknown,
    value: { id: number | undefined; label: string } | null,
  ) => {
    setSelectedLocation(value ?? { id: undefined, label: "" });
    setSelectedSubLocation({ id: undefined, label: "" });
  };

  const handleSubLocationChange = (
    _e: unknown,
    value: { id: number | undefined; label: string } | null,
  ) => {
    setSelectedSubLocation(value ?? { id: undefined, label: "" });
  };

  const handleSubmit = async () => {
    let article = articleInput;

    const parsedAmountPerContainer = toFloat(amountPerContainer);

    const apcChanged =
      Number.isFinite(parsedAmountPerContainer) &&
      parsedAmountPerContainer !== articleInput.amount_per_container;

    const isStandard = articleInput.is_standard_amount_per_container === true;

    if (apcChanged && isStandard) {
      const newArticle: NewArticle = {
        code: `${articleInput.code}-${parsedAmountPerContainer}`,
        description: `${articleInput.description} (AUTO GENERADO)`,
        amount_per_container: parsedAmountPerContainer,
        client_id: articleInput.client_id,
        unit_id: articleInput.unit_id,
        container_id: articleInput.container.id,
        weight: articleInput.weight,
        width: articleInput.width ?? null,
        height: articleInput.height ?? null,
        long: articleInput.long ?? null,
        amount: 0,
        units_per_pallet: articleInput.units_per_pallet ?? undefined,
        require_fecha_vencimiento:
          articleInput.require_fecha_vencimiento ?? false,
        is_standard_amount_per_container:
          articleInput.is_standard_amount_per_container ?? false,
        fce: articleInput.fce,
      };

      const response = await createArticle(newArticle);

      if (response.status === STATUS_CODES.OK) {
        article = response.data;
        addSessionData(ENTITY.ARTICLES, response.data);
      }
    }

    let location;

    if (selectedSubLocation?.id) {
      location = availableSubLocations.find(
        (sl) => sl.id === selectedSubLocation.id,
      );
    } else if (selectedLocation?.id && selectedLocation.id !== 0) {
      location = locations.find((l) => l.id === selectedLocation.id);
    }

    if (!location) return;

    const movementAPC =
      apcChanged && !isStandard
        ? parsedAmountPerContainer
        : article.amount_per_container;
    const notAvailableAmount =
      availabilityStatus === "NOT_AVAILABLE" ? amount : 0;

    const tempArticleLocationId = -(rowIndex + 1);
    const normalizedObservation = observation.trim() || null;

    const articleLocations: ArticleLocation = {
      id: tempArticleLocationId,
      article_id: article.id,
      location_id: location.id,
      article,
      location,
      stock: amount,
      reserved: 0,
      not_available: notAvailableAmount,
      amount_per_container: movementAPC,
      lote: lote ?? null,
      oc: ordenCompra ?? null,
      fecha_vencimiento: dueDate ?? null,
      observation: normalizedObservation,
      observation_available:
        availabilityStatus === "AVAILABLE" ? normalizedObservation : null,
      observation_not_available:
        availabilityStatus === "NOT_AVAILABLE" ? normalizedObservation : null,
    };

    const movArt: MovementArticle = {
      id: rowIndex,
      article_locations: articleLocations,
      article_locations_id: articleLocations.id,
      stock_state:
        availabilityStatus === "NOT_AVAILABLE" ? "not_available" : "available",
      article_id: articleLocations.article_id,
      client_id: articleLocations.article.client.id,
      location_id: articleLocations.location_id,
      container: articleLocations.article.container.description,
      container_id: articleLocations.article.container.id,
      description: articleLocations.article.description,
      weight: article.weight,
      volume_weight: article.container?.volume_weight ?? 0,
      unit: articleLocations.article.unit.description,
      unit_id: articleLocations.article.unit.id,
      presentation: articleLocations.article.presentation ?? null,
      amount_per_container: movementAPC,
      amount,
      subtotal: movementAPC * amount,
      subtotal_kg: amount * article.weight,
    };

    onMovementArticleAdd(movArt);
    setAmount(1);
  };

  const parsedAPC = useMemo(() => {
    const n = toFloat(amountPerContainer);
    return Number.isFinite(n) ? n : null;
  }, [amountPerContainer]);

  const expectedArticleCode = useMemo(() => {
    if (parsedAPC != null && parsedAPC !== articleInput.amount_per_container) {
      return `${articleInput.code}-${parsedAPC}`;
    }
    return articleInput.code;
  }, [articleInput.code, articleInput.amount_per_container, parsedAPC]);

  const sameArticle = (al: ArticleLocation) => {
    const ex = al.article;
    if (!ex) return false;
    if (parsedAPC == null || parsedAPC === articleInput.amount_per_container) {
      return ex.id === articleInput.id || ex.code === articleInput.code;
    }
    return ex.code === expectedArticleCode;
  };

  const alreadyAdded = useMemo(() => {
    const locId = selectedLocation?.id ?? null;
    if (!locId) return false;

    const cand = {
      locId,
      lote: normStr(lote),
      oc: normStr(ordenCompra),
      fv: normDate(dueDate),
      isNotAvailable: availabilityStatus === "NOT_AVAILABLE",
    };

    const res = movement.articles.some((ma) => {
      const al = ma.article_locations;
      if (!al) return false;

      const ex = {
        locId: al.location_id ?? al.location?.id ?? null,
        lote: normStr(al.lote as any),
        oc: normStr(al.oc as any),
        fv: normDate(al.fecha_vencimiento as any),
        isNotAvailable: Number(al.not_available ?? 0) > 0,
      };

      return (
        sameArticle(al) &&
        ex.locId === cand.locId &&
        ex.lote === cand.lote &&
        ex.oc === cand.oc &&
        ex.fv === cand.fv &&
        ex.isNotAvailable === cand.isNotAvailable
      );
    });

    return res;
  }, [
    movement.articles,
    selectedLocation?.id,
    lote,
    ordenCompra,
    dueDate,
    articleInput.id,
    articleInput.code,
    articleInput.amount_per_container,
    availabilityStatus,
    parsedAPC,
  ]);

  const isButtonDisabled = useCallback(() => {
    const basicInvalid = !amount || amount <= 0 || !selectedLocation.id;
    const requireFV = articleInput.require_fecha_vencimiento === true;
    const fvMissing = requireFV && !dueDate;
    const needsSubLocation = availableSubLocations.length > 0;
    const subMissing = needsSubLocation && !selectedSubLocation?.id;

    return basicInvalid || alreadyAdded || fvMissing || subMissing;
  }, [
    selectedLocation,
    amount,
    alreadyAdded,
    articleInput.require_fecha_vencimiento,
    dueDate,
  ]);

  useEffect(() => {
    if (selectedLocation?.id === 0) {
      setAddNewLocationModalOpen(true);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (!mainLocationId) return;

    const loc = locations.find((l) => l.id === mainLocationId);
    if (!loc) return;

    setSelectedLocation({
      id: loc.id,
      label: loc.code,
    });
  }, [mainLocationId, locations]);

  return (
    <>
      <TableRow>
        <TableCell align="center">{articleInput.code}</TableCell>
        <TableCell align="center">{articleInput.description}</TableCell>
        <TableCell align="center">{articleInput.container.description}</TableCell>
        <TableCell align="center">{articleInput.units_per_pallet}</TableCell>
        <TableCell align="center">{articleInput.unit.description}</TableCell>
        <TableCell align="center">{articleInput.presentation ?? "-"}</TableCell>
        <TableCell align="center">
          <CustomNumberInput
            data-cy={`qty-${articleInput.code}`}
            label={`Cantidad a ingresar`}
            value={amount}
            onChange={handleAmountChange}
          />
        </TableCell>
        <TableCell align="center">
          <Autocomplete
            data-cy={`location-${articleInput.code}`}
            autoComplete
            disableClearable
            style={{ marginTop: 6 }}
            options={locationOptions}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={selectedLocation}
            onChange={handleLocationChange}
            renderInput={(params) => (
              <TextField {...params} required label="Seleccione" />
            )}
            renderOption={(props, option) => (
              <li {...props} data-cy={option.label}>
                {option.label}
              </li>
            )}
          />
        </TableCell>
        <TableCell align="center">
          <Autocomplete
            data-cy={`sublocation-${articleInput.code}`}
            autoComplete
            disableClearable
            style={{ marginTop: 6 }}
            options={subLocationOptions}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={selectedSubLocation}
            onChange={handleSubLocationChange}
            disabled={!isSubLocationEnabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccione"
                placeholder={
                  !selectedLocation?.id
                    ? "Elegí ubicación"
                    : isSubLocationEnabled
                      ? ""
                      : "Sin sub-ubicaciones"
                }
              />
            )}
          />
        </TableCell>
        <TableCell align="center">
          <CustomNumberInput
            data-cy={`amount-per-container-${articleInput.code}`}
            label={`Cant. por Contenedor`}
            value={amountPerContainer}
            required
            onChange={(e) => setAmountPerContainer(e.target.value)}
          />
        </TableCell>
        <TableCell align="center">
          <Select
            data-cy={`status-${articleInput.code}`}
            value={availabilityStatus}
            onChange={(e) =>
              setAvailabilityStatus(e.target.value as AvailabilityStatus)
            }
          >
            <MenuItem value="AVAILABLE">Disponible</MenuItem>
            <MenuItem value="NOT_AVAILABLE">No Disponible</MenuItem>
          </Select>
        </TableCell>
        {showLote && (
          <TableCell align="center">
            <TextField
              data-cy={`lote-${articleInput.code}`}
              label="Lote"
              type="text"
              onChange={(e) => setLote(e.target.value)}
            />
          </TableCell>
        )}
        {showOC && (
          <TableCell align="center">
            <TextField
              data-cy={`order-${articleInput.code}`}
              label={`OC`}
              type="text"
              onChange={(e) => setOrdenCompra(e.target.value)}
            />
          </TableCell>
        )}
        {showFechaVencimiento && (
          <TableCell align="center">
            <DateTimePicker
              label="Fecha"
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slots={{
                actionBar: DateTimePickerActionBar,
                textField: TextField,
              }}
              slotProps={{
                textField: {
                  inputProps: {
                    "data-cy": `exp-date-${articleInput.code}`,
                  },
                },
              }}
              views={DATE_VIEWS}
              onChange={(value: Date | null) => {
                try {
                  if (value?.toISOString()) {
                    setDueDate(dateISOFormat(value.toISOString()) || undefined);
                  }
                } catch (dateExcep) {
                  return;
                }
              }}
            />
          </TableCell>
        )}
        <TableCell align="center">
          <TextField
            label="Observación"
            multiline
            minRows={1}
            maxRows={3}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
          />
        </TableCell>
        <TableCell align="center">
          <Tooltip
            disableHoverListener={!isButtonDisabled()}
            title={
              !selectedLocation.id
                ? "Seleccioná una ubicación."
                : amount <= 0
                  ? "Ingresá una cantidad mayor a 0."
                  : alreadyAdded
                    ? "Ya cargaste esta misma combinación (artículo/ubicación/lote/OC/fecha/estado)."
                    : articleInput.require_fecha_vencimiento && !dueDate
                      ? "Este artículo requiere fecha de vencimiento."
                      : availableSubLocations.length > 0 &&
                          !selectedSubLocation?.id
                        ? "Esta ubicación está fraccionada. Seleccioná una sub-ubicación."
                        : ""
            }
          >
            <span>
              <Button
                data-cy={`add-mov-art-${articleInput.code}`}
                variant="contained"
                disabled={isButtonDisabled()}
                onClick={handleSubmit}
              >
                <RxPlus size={"1.2rem"} />
              </Button>
            </span>
          </Tooltip>

          {movement.store_id && (
            <AddLocationModal
              open={addNewLocationModalOpen}
              handleOpen={() => {
                setSelectedLocation({ id: undefined, label: "" });
                setAddNewLocationModalOpen(false);
              }}
              onCreate={handleCreateLocation}
              stores={stores.filter((store) => store.id === movement.store_id)}
            />
          )}
        </TableCell>
      </TableRow>
    </>
  );
}

